import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";

import { AgentKeysManager, type AgentKeysClient } from "@/components/agent-keys";
import { PageContainer, PageHeader } from "@/components/layout";
import config from "../../config";
import { Features } from "../../entities/features";
import { Permissions } from "../../entities/permissions";
import { hasPermission } from "../login/selectors";
import {
  useCreateAgentKeyMutation,
  useLazyAgentKeysAuditQuery,
  useLazyAgentKeysQuery,
  useRevokeAgentKeyMutation,
  useRotateAgentKeyMutation,
} from "../../services/api";
import { useIsFeatureEnabled } from "../../services/api/featuresApi";

export default function Integrations() {
  const { t } = useTranslation();
  const canManage = useSelector(hasPermission(Permissions.PAGE_INTEGRATIONS_ROOT));
  const enabled = useIsFeatureEnabled(Features.FT_MCP);

  const [listKeys] = useLazyAgentKeysQuery();
  const [listAudit] = useLazyAgentKeysAuditQuery();
  const [createKey] = useCreateAgentKeyMutation();
  const [rotateKey] = useRotateAgentKeyMutation();
  const [revokeKey] = useRevokeAgentKeyMutation();

  const client = useMemo<AgentKeysClient>(
    () => ({
      list: () => listKeys(undefined, false).unwrap(),
      audit: (params) => listAudit(params, false).unwrap(),
      create: (input) => createKey(input).unwrap(),
      rotate: (id) => rotateKey({ id }).unwrap(),
      revoke: async (id) => {
        await revokeKey(id).unwrap();
      },
    }),
    [listKeys, listAudit, createKey, rotateKey, revokeKey],
  );

  if (!canManage || !enabled) {
    return <Navigate to="/dashboard/app" replace />;
  }

  return (
    <PageContainer title={t("integrations.tab-title")}>
      <PageHeader title={t("integrations.page-title")} description={t("integrations.description")} />
      <div className="mt-6">
        <AgentKeysManager client={client} agentBaseUrl={config.agentBaseUrl} />
      </div>
    </PageContainer>
  );
}
